// System-authored rows are a SystemMessage line, except the cards that own their row.
import type { Message } from "$lib/types.js";
import { findUrls } from "$lib/url-boundary.js";
import { workSessionNoticeOf } from "./work-session-notice.js";

export function rendersAsSystemLine(
  message: Pick<Message, "fromType" | "card"> &
    Partial<Pick<Message, "text" | "source" | "createdAt">>,
): boolean {
  return (
    message.fromType === "system" &&
    message.card?.kind !== "widget_notice" &&
    message.card?.kind !== "agents_joined" &&
    !workSessionNoticeOf({ text: "", createdAt: 0, ...message })
  );
}

// The retired "Saved as a reminder / to memory / as a standing order" receipt (CYBORG-1078).
// Nothing posts one any more; a stored legacy row draws nothing, its card or its fallback text.
export function isRetiredReceipt(message: { card?: { kind: string } | null }): boolean {
  return message.card?.kind === "memory_receipt";
}

// The retired per-slot "did not run its scheduled task" channel line: missed runs are one Alerts row
// for the agent's owner now (server schedule-home-offline-notice.ts). A stored row draws nothing.
const MISSED_RUN_LINE =
  / did not run its scheduled task: its home machine is (offline|no longer part of this workspace)/;
export function isMissedRunLine(message: Pick<Message, "fromType"> & { text?: string }): boolean {
  return message.fromType === "system" && MISSED_RUN_LINE.test(message.text ?? "");
}

// A "Post in #updates?" share card that was answered or expired: it leaves the DM for every viewer.
// The decision stays in Alerts and the agent's Activity tab.
export function isAnsweredShareCard(message: { card?: Message["card"] }): boolean {
  const card = message.card;
  return card?.kind === "approval" && !!card.holdId && !!card.resolution;
}

// Text + http(s) link runs only: system copy interpolates user-chosen names, so no markdown.
export function linkSegments(text: string): Array<{ text: string; href?: string }> {
  const out: Array<{ text: string; href?: string }> = [];
  let cursor = 0;
  for (const { start, end } of findUrls(text)) {
    if (start > cursor) out.push({ text: text.slice(cursor, start) });
    const url = text.slice(start, end);
    out.push({ text: url, href: url });
    cursor = end;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor) });
  return out;
}

// `messages.source` of the line a paused agent chain posts (server agent-hop-budget.ts).
export const HOP_LIMIT_SOURCE = "agent_hop_limit";

// Who may press Continue: anyone who may post in the thread. The relay's send_message rule
// (member and up) decides; a viewer or an external (role null) sees the line without it.
export function canContinueHop(role: string | null | undefined): boolean {
  return role === "owner" || role === "admin" || role === "member";
}

// A refused Continue whose pause is already lifted (someone else continued) just hides the button.
export function hopAlreadyContinued(err: unknown): boolean {
  return (err as { code?: unknown } | null)?.code === "conflict";
}

// A Continue whose resume did not run (the agent's machine is offline): the pause stays and the relay's
// message says why (server HOP_RESUME_FAILED); null for any other failure.
export function hopResumeFailure(err: unknown): string | null {
  const e = err as { code?: unknown; message?: unknown } | null;
  return e?.code === "hop_resume_failed" && typeof e.message === "string" ? e.message : null; // null: not this failure
}
